import Link from "next/link";

type MonicateringTextLinkProps = {
  children: React.ReactNode;
  href?: string;
  className?: string;
  target?: string;
  rel?: string;
  /** `span` — gdy CTA jest wewnątrz innego Linka; `button` — submit formularza. */
  as?: "link" | "span" | "button";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** `onDark` — jasny tekst na ciemnym tle; `journal` — burgundy Rumaya. */
  tone?: "default" | "onDark" | "journal";
  /** Kierunek strzałki — domyślnie w prawo (`end`). */
  arrow?: "start" | "end";
  /**
   * `retract` — linia widoczna, na hover zwija się (domyślne CTA).
   * `reveal` — bez linii, na hover wjeżdża z lewej.
   */
  underline?: "retract" | "reveal";
};

function sentenceCaseLabel(label: string) {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function formatLabel(children: React.ReactNode) {
  if (typeof children === "string") {
    return sentenceCaseLabel(children);
  }
  return children;
}

const toneClass = {
  default:
    "text-on-secondary hover:text-[color:var(--accent-heading)]",
  onDark:
    "text-on-primary hover:text-[color:var(--accent-heading)]",
  journal: "text-[#8b3126] hover:text-[#6e271e]",
} as const;

/**
 * Editorial text CTA: podkreślenie w spoczynku; na hover accent + strzałka + zwijanie linii.
 */
export function MonicateringTextLink({
  children,
  href = "#",
  className = "",
  target,
  rel,
  as = "link",
  type = "button",
  disabled = false,
  onClick,
  tone = "default",
  arrow = "end",
  underline = "retract",
}: MonicateringTextLinkProps) {
  const rootClassName = `group/text-link relative inline-block max-w-max overflow-visible whitespace-nowrap transition-colors duration-500 ease-[cubic-bezier(0.87,0,0.13,1)] disabled:pointer-events-none disabled:opacity-50 ${toneClass[tone]} ${className}`;
  const arrowPointsEnd = arrow === "end";
  const weightClass = tone === "journal" ? "font-medium" : "font-normal";
  const underlineClass =
    underline === "reveal"
      ? "pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-current transition-transform duration-[800ms] ease-[cubic-bezier(0.87,0,0.13,1)] group-hover/text-link:scale-x-100"
      : "pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left bg-current transition-transform duration-[800ms] ease-[cubic-bezier(0.87,0,0.13,1)] group-hover/text-link:origin-right group-hover/text-link:scale-x-0";

  const content = (
    <>
      <span
        className={`inline-flex items-center pb-2 font-inter text-base leading-[1.4] ${weightClass}`}
      >
        <span>{formatLabel(children)}</span>
        {/* Stała szerokość slotu — animacja tylko transform, bez zmiany hitboxa (fix flicker) */}
        <span
          aria-hidden
          className="ml-2 inline-flex w-[3.75rem] shrink-0 items-center justify-start"
        >
          <span
            className={`inline-flex transition-transform duration-[800ms] ease-[cubic-bezier(0.87,0,0.13,1)] ${
              arrowPointsEnd
                ? "translate-x-0 group-hover/text-link:translate-x-2"
                : "translate-x-8 group-hover/text-link:translate-x-0"
            }`}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`block${arrowPointsEnd ? "" : " -scale-x-100"}`}
            >
              <path
                d="M7 12H17"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
              <path
                d="M13.5 8.5L17 12L13.5 15.5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </span>
      </span>
      <span aria-hidden className={underlineClass} />
    </>
  );

  if (as === "span") {
    return (
      <span className={rootClassName} aria-hidden>
        {content}
      </span>
    );
  }

  if (as === "button") {
    return (
      <button
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={rootClassName}
      >
        {content}
      </button>
    );
  }

  return (
    <Link href={href} target={target} rel={rel} className={rootClassName}>
      {content}
    </Link>
  );
}
